import map from 'lodash/map';
import find from 'lodash/find';
import filter from 'lodash/filter';
import reduce from 'lodash/reduce';
import reject from 'lodash/reject';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import indexOf from 'lodash/indexOf';
import compact from 'lodash/compact';
import each from 'lodash/each';
import values from 'lodash/values';
import forEach from 'lodash/forEach';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import omitBy from 'lodash/omitBy';

import { createSelector as createMemoizedSelector } from 'reselect';

import { getUuidFrom, getIdFrom } from 'zoomforth_codesbane_viewer/lib/id_utils.js';

import PageEntityFuzzySearcher from 'zoomforth_codesbane_viewer/lib/page_entity_fuzzy_searcher.js';

import { 
  doesOnlyNavToSubpage
} from 'zoomforth_codesbane_viewer/lib/app_actions.js';

import {
  getContentUuids
} from 'zoomforth_codesbane_viewer/lib/sections.js';

import {
  SECTION_TYPE_TABS
} from 'zoomforth_codesbane_viewer/const/sections.js';

import {
  MASK_PAGE_ENTITIES_STATE,
  SET_TRANSLATIONS_CURRENT_LANGUAGE_ISO
} from 'zoomforth_codesbane_editor/action_types.js';


const pageEntitiesViewerReducer = (state = {}, action)=> {

  switch (action.type) {

  case MASK_PAGE_ENTITIES_STATE: {
    const { newPageEntities } = action;
    const updatedState = cloneDeep(state);

    merge(updatedState, newPageEntities);

    return updatedState;
  }

  case SET_TRANSLATIONS_CURRENT_LANGUAGE_ISO: {
    return {
      ...state,
      translationsCurrentLanguageIso: action.value
    };
  }

  default: {
    return state;
  }

  }

};

export default pageEntitiesViewerReducer;


// Alphabetize, please

export const getAssetByIdent = (state, assetIdent)=>
  state.assetsByIdent[assetIdent];

export const getAssetForContent = (state, contentItem)=> {
  const { asset_id, asset_type } = contentItem;
  return state.assetsByIdent[`${asset_type}-${asset_id}`];
};

export const getContentItemByUuid = (state, uuid)=>
  state.pageContentItemsByUuid[uuid];

export const getBlocksData = (state) =>
  state.blocksData;

export const getContainerBlocksData = (state) =>
  filter(getBlocksData(state),
    blockData => blockData.blockType === 'container'
  );

export const getContentBlocksData = (state) =>
  filter(getBlocksData(state),
    blockData => blockData.blockType === 'content'
  );

export const getContentUuidsForAssetId = (state, assetId)=> {
  const contentItems = values(state.pageContentItemsByUuid);
  return map(filter(contentItems, ['asset_id', assetId]), 'uuid');
};

export const getCssClassesListForContent = (state, contentItem)=>
  contentItem.css_classes_list;

export const getCssClassesListForOverlay = (state, contentItem)=>
  contentItem.overlay_css_classes_list;

export const getExistentTargetsForAppAction = (state, { sectionId, contentUuid })=> {

  const contentItem = contentUuid && getContentItemByUuid(state, contentUuid);

  if (contentUuid) {
    if (contentItem) {

      // We look up the section for this content and do NOT use the `currentlyChosenSectionId`
      // because it *should* be null now, although it is possible that pages (mostly codesbane
      // development) are in an inconsistent state.
      const sectionForContent = getSectionForContent(state, contentItem);

      if (sectionForContent) {
        return { section: sectionForContent, contentItem };
      } 
    }
  }

  const section = sectionId && getSectionById(state, sectionId);
  
  if (sectionId) {
    if (section) {
      return { section, contentItem: null };
    } 
  }

  return {
    section: null,
    contentItem: null
  };
};

export const getIsInPageSearchEnabled = (state)=>
  !!state.isInPageSearchEnabled;

export const getIsSecondaryLogoEnabled = (state)=>
  !!state.isSecondaryLogoEnabled;

const getSearchableStateSubset = (state)=>
  ({
    menuItemsById: state.menuItemsById,
    pageContentItemsByUuid: state.pageContentItemsByUuid,
    sectionsById: state.sectionsById,
    subpagesById: state.subpagesById
  });

export const getPageEntityFuzzySearcher = createMemoizedSelector(
  getSearchableStateSubset,
  (state)=>
    new PageEntityFuzzySearcher(state)
);

export const getIsMobileRenderEnabled = (state)=>
  !!state.isMobileRenderEnabled;

export const getMenuItemById = (state, id)=>
  getMenuItemIdToMenuItemMap(state)[id];

export const getMenuItemBySubpageId = (state, subpageId) => 
  find(getMenuItemIdToMenuItemMap(state), (menuItem) => menuItem.appAction.subpageId === subpageId);

export const getMenuItemIdToMenuItemMap = (state)=>
  state.menuItemsById;

export const getOrderedContentUuidsForSection = (state, sectionOrId)=> {
  const sectionId = getIdFrom(sectionOrId);
  const ordereredContentUuidsBySectionId = getSectionIdToOrderedContentUuidsMap(state);
  return ordereredContentUuidsBySectionId[sectionId] || [];
};

export const getOrderedMainMenuItems = (state)=>
  map(state.orderedMenuItemIds, id => getMenuItemIdToMenuItemMap(state)[id]);

export const getOrderedSubmenuItemsForSubpage = (state, subpageOrId) => {
  const orderedSubmenuItemIdsBySubpageId = getSubpageIdToOrderedSubmenuItemIdsMap(state);
  return map(
    orderedSubmenuItemIdsBySubpageId[getIdFrom(subpageOrId)],
    (submenuItem)=>
      getMenuItemById(state, getIdFrom(submenuItem))
  );
};

export const getSectionIdToSectionMap = (state)=> {
  return omitBy(state.sectionsById, (section, id) => !section.type);
};

export const getPageContentUuidToSectionIdMap = createMemoizedSelector(
  getSectionIdToSectionMap,
  (sectionsById)=> {
    const pageContentUuidToSectionIdMap = {};
    each(sectionsById, (section, sectionId)=> {

      each(getContentUuids(section), uuid => {
        pageContentUuidToSectionIdMap[uuid] = sectionId;
      });

    });
    return pageContentUuidToSectionIdMap;
  }
);

export const getPageThemeUuid = (state)=>
  state.themeUuid;

export const getSiteViewerVersion = (state)=>
  state.siteViewerVersion;

export const getTranslationsIsEnabled = (state)=>
  state.translationsIsEnabled;

export const getTranslationsDefaultLanguageIso = (state)=>
  state.translationsDefaultLanguageIso;

export const getTranslationsAppliedLanguages = (state)=>
  state.translationsAppliedLanguages;

export const getTranslationsCurrentLanguageIso = (state)=>
  state.translationsCurrentLanguageIso || getTranslationsDefaultLanguageIso(state);

export const getTranslationsSelectedLanguageIsos = (state)=>
  state.translationsSelectedLanguageIsos || [];

export const getPageTitle = (state)=>
  state.title;
// Reselect memoized selectors cannot take arguments!
// https://github.com/reactjs/reselect#q-how-do-i-create-a-selector-that-takes-an-argument

export const getSecondaryLogoData = (state)=>
  state.secondaryLogoData;

export const getSectionById = (state, id)=>
  getSectionIdToSectionMap(state)[id];

export const getSectionForContent = (state, contentOrUuid)=> {
  const uuid = getUuidFrom(contentOrUuid);
  const sectionId = getPageContentUuidToSectionIdMap(state)[uuid];
  return getSectionById(state, sectionId);
};

export const getSectionGridPositions = (state, sectionOrId)=>
  getSectionById(state, getIdFrom(sectionOrId)).gridPositions;

const getSectionIdToOrderedContentUuidsMap = createMemoizedSelector(
  getSectionIdToSectionMap,
  (sectionsById)=>
    reduce(sectionsById, (accum, section, sectionId)=>
      ({
        ...accum,
        [sectionId]: getContentUuids(section)
      })
    , {})
);

export const getSubpageIdToSubpageMap = (state)=>
  state.subpagesById;

const getSubpageSlugToIdMap = (state)=>
  state.subpageIdBySlug;

export const getSectionIdToSubpageIdMap = createMemoizedSelector(
  getSubpageIdToSubpageMap,
  (subpagesById)=> {
    const sectionIdToSubpageIdMap = {};
    each(subpagesById, (subpage, subpageId)=> {
      each(subpage.orderedSectionIds, (sectionId)=> {
        sectionIdToSubpageIdMap[sectionId] = subpageId;
      });
    });
    return sectionIdToSubpageIdMap;
  }
);

export const getSectionIdToTabIdMap = createMemoizedSelector(
  getSectionIdToSectionMap,
  (sectionsById)=> {
    const sectionIdToTabIdMap = {};
    each(sectionsById, (section, sectionId)=> {
      if (section.type === SECTION_TYPE_TABS && section.tabs) {
        each(section.tabs, tab => {
          if (tab.content && tab.content.sectionId) {
            // Map nested section ID to the tab section ID that contains it
            sectionIdToTabIdMap[tab.content.sectionId] = sectionId;
          }
        });
      }
    });
    return sectionIdToTabIdMap;
  }
);

export const getSectionIndexInSubpage = (state, sectionOrId)=> {
  const sectionId = getIdFrom(sectionOrId);
  const subpage = getSubpageForSection(state, sectionId);
  const orderedSectionIds = subpage.orderedSectionIds;
  return orderedSectionIds.indexOf(sectionId);
};

export const getSectionOrderedContentItems = (state, sectionOrId)=>
  map(
    getSectionById(state, getIdFrom(sectionOrId)).orderedContentUuids,
    uuid => getContentItemByUuid(state, uuid)
  );

export const getSectionSingletonContentItem = (state, sectionOrId)=>
  getContentItemByUuid(
    state,
    getSectionById(state, getIdFrom(sectionOrId)).singletonContentUuid
  );

export const getSections = (state)=>
  values(getSectionIdToSectionMap(state));


export const getSectionsForSubpage = (state, subpageOrId)=> {
  const subpageId = getIdFrom(subpageOrId);
  return filter(map(getSectionIdToSubpageIdMap(state), (_subpageId, _sectionId) => {
    if (subpageId === _subpageId) {
      return _sectionId;
    }
  }));
};

export const getSelectedSubmenuItem = (state, subpage, contentItemOrUuid, sectionOrId)=> {
  const sectionId = sectionOrId ? getIdFrom(sectionOrId) : null;
  const contentUuid = contentItemOrUuid ? getUuidFrom(contentItemOrUuid) : null;
  const submenuItems = compact(getOrderedSubmenuItemsForSubpage(state, subpage));

  // if there is a contentUuid set, we can pretty easily deduce what submenu
  // item is selected.
  if (contentUuid) {
    return find(submenuItems, (item) => {
      return contentUuid === item.appAction.contentUuid;
    });
  }

  // sectionId is not always available for a submenu item but if it is we want
  // the contentUuid to be null so we know we're going to the actual section.
  if (sectionId) {
    return find(submenuItems, (item) => {
      return (
        sectionId === item.appAction.sectionId &&
        !item.appAction.contentUuid
      );
    });
  }

  return null;
};

export const getShouldShowHeader = (state)=>
  state.shouldShowHeader;

export const getShouldShowSocialLinks = (state)=>
  !!state.shouldShowSocialLinks;
  
export const getShouldShowTitle = (state)=>
  state.shouldShowTitle;

export const getSocialLinks = (state)=>
  state.socialLinks || [];


export const getIsInlineHeaderStyle = (state) => {
  const isInline = state.isInlineHeaderStyle;
  return isNil(isInline) ? true : isInline;
};

export const getDisplayAnchorLinks = (state) => 
  state.displayAnchorLinks;

export const getIsFullBleed = (state) => 
  state.isFullBleed;

export const getHeaderContentPosition = (state) => 
  state.headerContentPosition;

export const getSubpages = (state) =>
  values(getSubpageIdToSubpageMap(state));

export const getSubpageById = (state, id)=>
  getSubpageIdToSubpageMap(state)[`${id}`];

// We accept either a slug or an ID here since legacy subpages used IDs for routing and we still need to support them.
export const getSubpageBySlugOrId = (state, slugOrId)=> {
  const subpageId = getSubpageSlugToIdMap(state)[`${slugOrId}`] || slugOrId;
  
  return getSubpageById(state, subpageId);
};

export const getIsUsingLegacyHeader = (state) => {
  return isNil(state.isUsingLegacyHeader) ? true : state.isUsingLegacyHeader;
};

export const getSubpageDisplayName = (state, subpageOrId)=> {
  const subpageId = getIdFrom(subpageOrId);
  const targetSubpage = getSubpageById(state, subpageId);

  const matchedSubpage = find(getSubpagesWithDisplayNames(state), subpage => (
    subpage.id === targetSubpage.id
  ));

  return matchedSubpage ? matchedSubpage.displayName : null;
};

export const getSubpageForContent = (state, contentOrUuid)=> {
  const uuid = getUuidFrom(contentOrUuid);
  const sectionId = getPageContentUuidToSectionIdMap(state)[uuid];
  return getSubpageForSection(state, sectionId);
};

export const getSubpageForSection = (state, sectionOrId)=> {
  const sectionId = getIdFrom(sectionOrId);
  
  const subpageId = getSectionIdToSubpageIdMap(state)[sectionId];
  if (subpageId) {
    return getSubpageIdToSubpageMap(state)[subpageId];
  }
  
  const tabSectionId = getSectionIdToTabIdMap(state)[sectionId];
  if (tabSectionId) {
    return getSubpageForSection(state, tabSectionId);
  }
  
  throw new Error(`Section ${sectionId} not found in any subpage or tab`);
};

export const getSubpageIdToOrderedSubmenuItemIdsMap = createMemoizedSelector(

  getMenuItemIdToMenuItemMap,
  getSectionIdToOrderedContentUuidsMap,
  getSubpageIdToSubpageMap,
  (menuItemsById, ordereredContentUuidsBySectionId, subpagesById)=> {

    const submenuItemsBySubpageId = {};

    // Build the subpage to menu id mapping (un-ordered)
    each(menuItemsById, (menuItem)=> {
      if (menuItem.isSubmenuItem) {
        if (!submenuItemsBySubpageId[menuItem.appAction.subpageId]) {
          submenuItemsBySubpageId[menuItem.appAction.subpageId] = [];
        }
        submenuItemsBySubpageId[menuItem.appAction.subpageId].push(menuItem);
      }
    });

    const orderedSubmenuItemIdsBySubpageId = {};

    each(submenuItemsBySubpageId, (submenuItems, subpageId)=> {

      const subpage = subpagesById[subpageId];

      // We order submenu items by their order in a subpage
      // First we build a sorted list of sections and content. In order to avoid a
      // collision we prepend `section--` to sections.
      const orderedSubpageContent = compact(reduce(subpage.orderedSectionIds, 

        (accum, sectionId) => ([
          ...accum,
          `section--${sectionId}`,
          ...ordereredContentUuidsBySectionId[sectionId]
        ]), 

      []));

      orderedSubmenuItemIdsBySubpageId[subpageId] = 
        sortBy(submenuItems, submenuItem => {
          const contentUuid = submenuItem.appAction.contentUuid;
          const sectionId = `section--${submenuItem.appAction.sectionId}`;

          var idx = indexOf(orderedSubpageContent, contentUuid ? contentUuid : sectionId);

          // this typically means we have an external link in the list so we
          // push it to the bottom.
          if (idx < 0) {
            idx = 1000;
          }

          return idx;
        });

    });

    return orderedSubmenuItemIdsBySubpageId;
  }
);

export const getSubpagesWithDisplayNames = (state)=> {
  const menuItems = getOrderedMainMenuItems(state);
  const subpageMenuItems = filter(menuItems, menuItem =>
    doesOnlyNavToSubpage(menuItem.appAction)
  );
  return map(subpageMenuItems, ({ title, appAction })=>
    ({
      displayName: title,
      id: appAction.subpageId,
      ...getSubpageIdToSubpageMap(state)[appAction.subpageId]
    })
  );
};

export const getTileMobileOrderPriorityByUuid = (state, section) => {
  const tileMobileOrderPriorityByUuid = {};
  forEach(section.gridPositions, ({ uuid }) => {
    const contentItem = getContentItemByUuid(state, uuid);
    if (contentItem) {
      tileMobileOrderPriorityByUuid[uuid] = contentItem.tile_mobile_order_priority;
    }
  });

  return tileMobileOrderPriorityByUuid;
};

export const getVisibleSubpageCount = (state)=>
  size(reject(
    getSubpageIdToSubpageMap(state),
    'isHidden'
  ));

export const getPageScopedCss = (state)=>
  state.pageScopedCss;

export const getSectionGridTaxonomy = (state, sectionOrId)=>
  getSectionById(state, getIdFrom(sectionOrId)).gridTaxonomy || null;

export const getSectionGridTaxonomyCategory = (state, sectionOrId, categoryId)=>
  find(getSectionGridTaxonomy(state, sectionOrId).categories, ['id', categoryId]) || null;

export const getMainSectionForSubpage = (state, subpage)=> (
  subpage.mainSectionId ?
    getSectionById(state, subpage.mainSectionId) || null
  :
    null
);
